import { axiosWrapper } from '@/services/axiosWrapper';
import { createRuleHelper } from '@/helpers/rulesHelper';
import { IRule } from '@/classes/rules/IRule';
import { NumberRule } from '@/classes/rules/NumberRule';
import { MinMaxRule } from '@/classes/rules/MinMaxRule';
import { DefaultValue } from '@/classes/rules/DefaultValue';
import { BooleanRule } from '@/classes/rules/BooleanRule';
import { EnumRule } from '@/classes/rules/EnumRule';
import { TextRule } from '@/classes/rules/TextRule';
import { DateRule } from '@/classes/rules/DateRule';
import { OptionsRule } from '@/classes/rules/OptionsRule';
import { FieldUsage } from '@/classes/rules/FieldUsage';
import { localeDateFromUTC } from '@/helpers/timeHelper';
import { getValueFromPath, hasProperties } from '@/helpers/pathHelper';
import { INestedRule } from '@/classes/rules/INestedRule';
import { projectRules } from '@/helpers/rulesModules/buildProjectRules';
import _log from '@/helpers/log';
import { ProjectMeta } from '@/classes/ProjectMeta';

/**
 * Interface for specification object retrieved by the service
 */
export interface Specification {
  path: string;
  name: string;
  type: string;
  required?: boolean;
  displayed?: boolean;
  protected?: boolean;
  min?: number | string;
  max?: number | string;
  default_value?: number | string | boolean;
  unit?: string;
  conditional_display?: string;
  invalid_sizing?: boolean;
  invalid_pre_sizing?: boolean;
}

export interface Specifications {
  [key: string]: Specifications | Specification
}

interface CountriesSpecifications {
  [key: string]: CountrySpecifications
}

interface CountrySpecifications {
  'country.en': string;
  'country.fr': string;
  'country.Iso 3166-1 alpha-3': string;
}

function isSpecification(specification: Specifications | Specification): specification is Specification {
  return hasProperties(specification, 'name', 'path');
}

/**
 * Specification types that can be found
 */
enum specTypes {
  boolean = 'boolean',
  number = 'number',
  integer = 'integer',
  calculated = 'calculated',
  string = 'string',
  enum = 'enum',
  Date = 'Date'
}

interface Dependencies {
  dateGenerator: () => Date;
  fileLoader: <Type>(filename: string) => Promise<{ data: Type }>
  errorHandler: (errorMessage: string) => void
}

export const buildRuleService = ({ dateGenerator, fileLoader, errorHandler }: Dependencies) => {
  let countriesReferences: CountriesSpecifications | null = null;

  type Parameters = {
    rootProjectRules?: INestedRule<ProjectMeta>
  }

  async function initialiseProjectRules({ rootProjectRules = projectRules }: Parameters = {}): Promise<void> {
    const fieldsSpecs = await loadFieldsSpec();
    await assignSpecificationsToRules(fieldsSpecs.project, rootProjectRules);
  }

  function assignSpecificationsToRules<Context>(specifications: Specifications | undefined, rules: INestedRule<Context>): void {
    if (!specifications) {
      throw new Error('Fields specifications are empty');
    }
    const ruleHelper = createRuleHelper({ rules });
    assignEachSpecification(specifications, ruleHelper);
  }

  /**
   * Load countries default values
   */
  async function stockCountriesReferences(): Promise<CountriesSpecifications> {
    const references = await loadCountriesReferences();
    countriesReferences = references;
    return references;
  }

  /**
   * Assign default values of one country to rules
   * @param country3Ref string reference of the country
   * @param rules to update using specifications
   */
  function assignCountryReferences(country3Ref: string, rules: INestedRule): void {
    const country = countriesReferences?.[country3Ref];
    if (!country) {
      throw new Error(`Country with ISO reference ${country3Ref} can't be found`);
    }
    Object.entries(country)
      .filter(([path]) => path.startsWith('scenario'))
      .filter(([, value]) => value !== null)
      .map(([path, value]) => [retrieveRule(rules, path), value] as const)
      .filter((entry): entry is [IRule, unknown] => entry[0] !== undefined)
      .forEach(assignNewDefaultValue);
  }

  function retrieveRule(rules: INestedRule, path: string): IRule | undefined {
    const split = path.split('.');
    return createRuleHelper({ rules }).getFromRulesDictionary(split);
  }

  function assignNewDefaultValue([rule, value]: [IRule, unknown]): void {
    const defaultValue = new DefaultValue(value);
    const previousDefaultValue = rule.getDefaultValue();
    if (previousDefaultValue instanceof DefaultValue && previousDefaultValue.isLocked) {
      defaultValue.withLock();
    }
    rule.withDefaultValue(() => defaultValue);
  }

  /**
   * load functions, currently just retrieving a local json
   */
  async function loadFieldsSpec(): Promise<{ project: Specifications }> {
    const { data } = await fileLoader<{ project: Specifications }>('fields_specifications');
    return data;
  }

  async function loadCountriesReferences(): Promise<CountriesSpecifications> {
    const { data } = await fileLoader<CountriesSpecifications>('countries_references');
    return data;
  }

  function assignEachSpecification<Context>(tree: Specifications, ruleHelper: ReturnType<typeof createRuleHelper<Context>>): void {
    for (const treeElement of Object.values(tree)) {
      if (isSpecification(treeElement)) {
        const fullPath = treeElement.path.split('.').concat(treeElement.name);
        const targetedRule = ruleHelper.getFromRulesDictionary(fullPath);
        if (targetedRule) {
          assignSpecificationToRule(treeElement, targetedRule);
        } else {
          errorHandler(`Rule '${treeElement.path}.${treeElement.name}' does not exist`);
        }
      } else {
        assignEachSpecification(treeElement, ruleHelper);
      }
    }
  }

  /**
   * determine how to assign one given specification to the given rule.
   * @param specification the given specification
   * @param rule the rule to assign the specification to
   */
  function assignSpecificationToRule<Context>(specification: Specification, rule: IRule<Context>): void {
    if (!isConsistent(specification, rule)) {
      return;
    }
    [handleCommonRules, handleNumberRule, handleMinMaxRule, handleDateRules, handleEnumRule]
      .forEach(handler => handler(specification, rule));
  }

  function isConsistent<Context>({ type }: Specification, rule: IRule<Context>): boolean {
    return (type === specTypes.boolean && rule instanceof BooleanRule) ||
      (type === specTypes.enum && rule instanceof EnumRule) ||
      (type === specTypes.string && rule instanceof TextRule) ||
      (type === specTypes.Date && rule instanceof DateRule) ||
      ((type === specTypes.number || type === specTypes.integer || type === specTypes.calculated) && rule instanceof NumberRule) ||
      rule instanceof OptionsRule;
  }

  function handleCommonRules<Context>(specification: Specification, rule: IRule<Context>): void {
    const { required, displayed, invalid_sizing, invalid_pre_sizing } = specification;
    rule.required = required ?? rule.required;
    rule.displayed = displayed ?? rule.displayed;
    assignDefaultValueToRule(specification, rule);
    if (invalid_sizing !== undefined && invalid_pre_sizing !== undefined) {
      rule.withFieldUsage(new FieldUsage({
        ...rule.fieldUsage,
        preSizing: invalid_pre_sizing,
        sizing: invalid_sizing
      }));
    }
  }

  function handleNumberRule<Context>({ type, unit }: Specification, rule: IRule<Context>): void {
    if (rule instanceof NumberRule) {
      rule.unitString = unit ?? rule.unitString;
      const factor = unit?.startsWith('%') ? 100 : rule.factor;
      rule.withFactor(factor);
      if (type === specTypes.integer) {
        rule.withIsInteger();
      }
    }
  }

  function handleMinMaxRule<Context>({ max, min }: Specification, rule: IRule<Context>): void {
    if (rule instanceof MinMaxRule) {
      rule.min = min ?? rule.min;
      rule.max = max ?? rule.max;
    }
  }

  function handleEnumRule<Context>({ default_value }: Specification, rule: IRule<Context>): void {
    if (!(rule instanceof EnumRule)) {
      return;
    }
    if (typeof default_value === 'string' && !rule.enumRef[default_value]) {
      throw new Error(`Value '${default_value}' does not exist in [${Object.keys(rule.enumRef)}]`);
    }
  }

  function handleDateRules<Context>({ max, min }: Specification, rule: IRule<Context>): void {
    if (!(rule instanceof DateRule)) {
      return;
    }
    if (typeof min === 'string') {
      rule.min = getDateValueFromString(min);
    }
    if (typeof max === 'string') {
      rule.max = getDateValueFromString(max);
    }
  }

  /**
   * Common method for default values assignation
   * @param specification the specification to assign
   * @param rule the rule to assign the specification to
   */
  function assignDefaultValueToRule<Context>(specification: Specification, rule: IRule<Context>): void {
    const { default_value, protected: isProtected = false } = specification;
    if (default_value === undefined) {
      return;
    }
    rule.withDefaultValue((scenario?: Context) => {
      const rawDefaultValue = getRawDefaultValue(specification, scenario);
      return rawDefaultValue !== null ? new DefaultValue(rawDefaultValue, isProtected) : null;
    });
  }

  function getRawDefaultValue<Context>({ default_value, type }: Specification, scenario?: Context): unknown {
    if (type === specTypes.Date && typeof default_value === 'string') {
      return getDateValueFromString(default_value);
    }
    const [root, ...path] = readPath(default_value);
    if (root === 'scenario') {
      return getValueFromPath(scenario, path);
    }
    return default_value;
  }

  // UTILITIES =========================================================
  /**
   * try to recognize a custom date pattern from a string, or just create a new Date
   * @param dateString the analysed string
   */
  function getDateValueFromString(dateString: string): Date | string {
    const [generator, ...instructions] = readPath(dateString);
    if (generator === 'now') {
      return instructions.reduce(applyInstruction, dateGenerator());
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return dateString;
    }
    return localeDateFromUTC(date);
  }

  function readPath(value: unknown): string[] {
    if (typeof value === 'string') {
      return value.split('.');
    }
    return [];
  }

  /**
   * Creates a new date based on existing date and instruction
   * @param existingDate the date to modify
   * @param dateInstruction the instruction to update the date
   */
  function applyInstruction(existingDate: Date, dateInstruction: string): Date {
    const modifiedDate = new Date(existingDate);
    if (dateInstruction.endsWith('year')) {
      const yearsInstruction = dateInstruction.replace('year', '');
      const years = Number.parseInt(yearsInstruction, 10);
      modifiedDate.setFullYear(modifiedDate.getFullYear() + years);
    } else if (dateInstruction.endsWith('minute')) {
      const minutesInstruction = dateInstruction.replace('minute', '');
      if (minutesInstruction === '0') {
        modifiedDate.setMinutes(0);
      }
    }
    return modifiedDate;
  }

  return {
    initialiseProjectRules,
    assignSpecificationsToRules,
    stockCountriesReferences,
    assignCountryReferences,
    loadFieldsSpec,
    assignSpecToRule: assignSpecificationToRule,
    getDateValueFromString
  };
};

export default buildRuleService({
  dateGenerator: () => new Date(),
  fileLoader: axiosWrapper.getFromLocalJson,
  errorHandler: _log.logError
});
